import { useEffect } from "react";
import { useLocation } from "react-router-dom";
import {
  upsertMetaByName,
  upsertMetaByProperty,
  upsertCanonical,
} from "../lib/seoHead";
import { SEO_META, SITE_URL } from "../config/seoMeta";

/**
 * SeoApplier — applies title/meta from `seoMeta.js` on every route change.
 *
 * Runs in a slight delay so it overrides page-level SEO useEffects that
 * may have set their own values. This way the spreadsheet meta wins for
 * all paths defined in SEO_META.
 *
 * Dynamic pages (/Latest_update/:id, /typing-test/:id etc.) are NOT in
 * SEO_META and continue to manage their own per-record meta.
 */
export default function SeoApplier() {
  const { pathname } = useLocation();

  useEffect(() => {
    const meta = SEO_META[pathname];
    if (!meta) return;

    const apply = () => {
      document.title = meta.title;
      upsertMetaByName("description", meta.description);
      upsertCanonical(`${SITE_URL}${pathname === "/" ? "/" : pathname}`);
      upsertMetaByProperty("og:title", meta.title);
      upsertMetaByProperty("og:description", meta.description);
      upsertMetaByProperty("og:url", `${SITE_URL}${pathname}`);
      upsertMetaByName("twitter:title", meta.title);
      upsertMetaByName("twitter:description", meta.description);
    };

    // Initial apply
    apply();
    // Run again after a microtask + animation frame so we override any
    // page-level useEffects that fire in the same tick.
    const t = setTimeout(apply, 80);
    return () => clearTimeout(t);
  }, [pathname]);

  return null;
}
